import { Link } from 'expo-router';
import ArrowRight from 'lucide-react-native/icons/arrow-right';
import { Pressable, StyleSheet, Text, View } from 'react-native';
import { useTranslation } from 'react-i18next';

import { useTheme } from '@/hooks/use-theme';
import type { BoatTypePage } from '@/lib/boat-rental';
import { boatRentalCityCopy, boatTypePageTitle } from '@/lib/i18n/boat-rental-content';
import type { Locale } from '@todo/core/i18n/locales';

// Stands in for the site-wide boat FAQ on a boat detail page. That FAQ was the
// same text on all ~2,400 boat pages, which made them look like duplicate
// content — so the detail page links to its city's /boat-rental/[city] page
// instead, whose FAQ is built from that city's own numbers. Only render it when
// the city has an exported page (useRelatedBoats' `hasCityPage`). When the
// boat's type has its own city page too (`typePage`), that comes first — it's
// the closer match ("catamaran rental in Cannes" for a catamaran).
export function BoatCityLink({
  cityId,
  locale,
  typePage = null,
}: {
  cityId: string;
  locale: Locale;
  typePage?: BoatTypePage | null;
}) {
  const theme = useTheme();
  const { t } = useTranslation();
  const copy = boatRentalCityCopy(locale, cityId);
  if (!copy) return null;
  const typeTitle = typePage ? boatTypePageTitle(locale, typePage, cityId) : undefined;

  const cityLink = (
    <Link
      href={{ pathname: '/[lang]/boat-rental/[city]', params: { lang: locale, city: cityId } }}
      asChild>
      <Pressable accessibilityRole="link" style={styles.link}>
        {({ pressed }) => (
          <>
            <Text style={[styles.text, { color: theme.primary }, pressed && { opacity: 0.6 }]}>
              {t('boatRental.detailLink', { inCity: copy.inCity })}
            </Text>
            <ArrowRight size={16} color={theme.primary} style={pressed && { opacity: 0.6 }} />
          </>
        )}
      </Pressable>
    </Link>
  );
  if (!typePage || !typeTitle) return cityLink;

  return (
    <View style={styles.links}>
      <Link
        href={{
          pathname: '/[lang]/boat-rental/[city]/[type]',
          params: { lang: locale, city: cityId, type: typePage },
        }}
        asChild>
        <Pressable accessibilityRole="link" style={styles.link}>
          {({ pressed }) => (
            <>
              <Text style={[styles.text, { color: theme.primary }, pressed && { opacity: 0.6 }]}>
                {t('boatRental.typeDetailLink', { title: typeTitle })}
              </Text>
              <ArrowRight size={16} color={theme.primary} style={pressed && { opacity: 0.6 }} />
            </>
          )}
        </Pressable>
      </Link>
      {cityLink}
    </View>
  );
}

const styles = StyleSheet.create({
  links: {
    gap: 8,
  },
  link: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  text: {
    fontSize: 14,
    fontWeight: '600',
    flexShrink: 1,
  },
});
